import {yupResolver} from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {Controller, useForm} from 'react-hook-form';
import {Form, Popup, List, Container, Icon, Button} from 'semantic-ui-react';
import * as yup from 'yup';

import {useReservedHostnames} from 'hooks';

import GroupSearchField from '../Common/Forms/GroupSearchField';
import {
  categoryOptions,
  categoryPurposes,
  validationSchemas,
  buildSubdomainDropdownOptions,
} from '../Common/sharedElements';

import styles from './GitlabPagesSiteCreation.module.scss';

const formSchema = yup.object().shape({
  category: validationSchemas.categoryValidation,
  name: validationSchemas.nameValidation.test(
    'domainIsUnique',
    'Such domain already exists',
    (value, ctx) => {
      const siteDomain = value + ctx.parent.subdomain;

      if (ctx.options.context.reservedHostnames.includes(value)) {
        return ctx.createError({message: 'Provided site name is a reserved name'});
      }

      return !ctx.options.context.reservedFullyQualifiedDomainNames.includes(siteDomain);
    }
  ),
  subdomain: validationSchemas.subdomainValidation,
  adminGroup: yup.string(),
  description: validationSchemas.descriptionValidation,
  computingRulesApproved: yup.boolean().oneOf([true]),
});

const defaultFormValues = {
  category: 'Official',
  name: '',
  subdomain: '.docs.cern.ch',
  adminGroup: '',
  description: '',
  computingRulesApproved: false,
};

const GitlabPagesSiteCreationForm = props => {
  const {
    isLoading: isLoadingReservedHostnames,
    reservedHostnames,
    reservedFullyQualifiedDomainNames,
  } = useReservedHostnames('gps');
  const {
    control,
    handleSubmit,
    formState: {isValid, isSubmitting},
    trigger,
    watch,
  } = useForm({
    context: {reservedHostnames, reservedFullyQualifiedDomainNames},
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(formSchema),
  });

  const [categoryValue, adminGroupValue] = watch(['category', 'adminGroup']);

  // Form submission handler, sends a POST request via a function passed from parent component in props.create
  const submitHandler = async data => {
    const project = {
      name: data.name,
      description: data.description,
      category: data.category,
    };

    if (data.adminGroup) {
      project.adminGroup = data.adminGroup;
    }

    await props.create({
      project: project,
      name: data.name,
      subdomain: data.subdomain,
    });
  };

  return (
    <Container className={styles.GitlabPagesSiteCreationForm}>
      <a
        href="https://webeos.docs.cern.ch/create_site/#site-category"
        target="_blank"
        rel="noreferrer"
      >
        Our recommendation for choosing a category <Icon name="external alternate" size="small" />
      </a>
      <Form onSubmit={handleSubmit(submitHandler)} loading={isSubmitting}>
        <Controller
          control={control}
          name="category"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Popup
              trigger={
                <Form.Dropdown
                  onChange={(_evt, data) => onChange(data.value)}
                  name={name}
                  value={value}
                  options={categoryOptions}
                  error={error?.message}
                  fluid
                  selection
                  required
                />
              }
              position="left center"
              wide
            >
              <Popup.Header>Specify site's category:</Popup.Header>
              <Popup.Content>
                <List items={categoryPurposes} />
                <p>The category can be changed later.</p>
              </Popup.Content>
            </Popup>
          )}
        />

        <Form.Group>
          <Controller
            control={control}
            name="name"
            render={({field: {onChange, value, name}, fieldState: {error}}) => (
              <Popup
                trigger={
                  <Form.Input
                    id="siteName"
                    label={{
                      htmlFor: 'siteName',
                      className: styles.VisuallyHidden,
                      children: 'Site name',
                    }}
                    onChange={(_evt, data) => onChange(data.value)}
                    name={name}
                    value={value}
                    width={10}
                    placeholder="Site name"
                    error={error?.message}
                    required
                    fluid
                  />
                }
                position="left center"
              >
                <Popup.Content>
                  Choose descriptive and short name, following naming conventions.
                </Popup.Content>
              </Popup>
            )}
          />

          <Controller
            control={control}
            name="subdomain"
            render={({field: {onChange, value, name}, fieldState: {error}}) => (
              <Popup
                trigger={
                  <Form.Dropdown
                    onChange={(_evt, data) => {
                      onChange(data.value);
                      // trigger validation of the name field
                      trigger('name');
                    }}
                    name={name}
                    value={value}
                    width={6}
                    options={buildSubdomainDropdownOptions().filter(
                      subdomain => subdomain.key !== '.webtest.cern.ch'
                    )}
                    // Remove this filter once webEOS migration support ends
                    // https://gitlab.cern.ch/webservices/webservices-portal/-/issues/197
                    error={error?.message}
                    required
                    fluid
                    selection
                  />
                }
                position="right center"
                on="hover"
              >
                <Popup.Content>
                  Choose a subdomain. Together with site name it will form the site url. You will
                  add this url in the GitLab repository in the next step.
                </Popup.Content>
              </Popup>
            )}
          />
        </Form.Group>

        <Controller
          control={control}
          name="adminGroup"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <GroupSearchField
              id="adminGroup"
              name={name}
              value={value}
              onChange={data => {
                onChange(data.groupIdentifier ? data.groupIdentifier : '');
              }}
              error={error?.message}
              showRecommendation={categoryValue === 'Official' && !adminGroupValue}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({field: {onChange, value, name}, fieldState: {error}}) => (
            <Popup
              trigger={
                <Form.Input
                  id="siteDescription"
                  label={{
                    htmlFor: 'siteDescription',
                    className: styles.VisuallyHidden,
                    children: 'Site description',
                  }}
                  onChange={(_evt, data) => onChange(data.value)}
                  name={name}
                  value={value}
                  error={error?.message}
                  placeholder="Site description"
                  required
                />
              }
              position="left center"
            >
              <Popup.Content>Description of the site's purpose.</Popup.Content>
            </Popup>
          )}
        />
        <Controller
          control={control}
          name="computingRulesApproved"
          render={({field: {onChange, value, name}}) => (
            <Form.Checkbox
              id="computingRules"
              onChange={(_evt, data) => onChange(data.checked)}
              name={name}
              checked={value}
              label={
                <label htmlFor="computingRules">
                  I have read and agreed to the{' '}
                  <a
                    href="https://security.web.cern.ch/security/rules/en/index.shtml"
                    target="_blank"
                    rel="noreferrer"
                  >
                    CERN Computing Rules
                  </a>{' '}
                  and taken into account the{' '}
                  <a
                    href="http://design-guidelines.web.cern.ch/guidelines-cern-websites"
                    target="_blank"
                    rel="noreferrer"
                  >
                    design guidelines
                  </a>{' '}
                  for websites and the{' '}
                  <a href="https://webeos.docs.cern.ch/lifecycle/" target="_blank" rel="noreferrer">
                    website lifecycle policy
                  </a>
                  .
                </label>
              }
            />
          )}
        />
        <Button
          type="submit"
          className={styles.SaveButton}
          floated="right"
          disabled={!isValid || isLoadingReservedHostnames || isSubmitting}
        >
          Create
        </Button>
      </Form>
    </Container>
  );
};

GitlabPagesSiteCreationForm.propTypes = {
  create: PropTypes.func.isRequired,
};

export default GitlabPagesSiteCreationForm;
